import { queryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { gqlClient } from "../../../shared/lib/gql-client";
import type { Area, AreaDayOutlook } from "../types";

/**
 * Fields the forecast chip and detail row need.
 *
 * Exported and reused wherever a forecast is selected, so the list and the detail
 * page cannot drift apart — the events package had exactly that bug, where a
 * duplicated field list silently lost `tags`.
 */
export const FORECAST_FIELDS = gql`
  fragment ForecastFields on Forecast {
    forecastDate
    tempMinC
    tempMaxC
    symbolCode
    precipitationMm
    # Shown as the range on the detail page; confidence is the compact form.
    # (No backticks in here — they would end the enclosing template literal.)
    tempP10C
    tempP90C
    precipProbabilityPct
    confidence
  }
`;

/**
 * Typical-conditions fields, for events beyond the forecast horizon.
 *
 * `sampleYears` is selected because the chip states its own basis — "10-year
 * average" — rather than asking the reader to take the number on trust.
 */
export const CLIMATE_FIELDS = gql`
  fragment ClimateFields on Climate {
    monthDay
    tempMaxAvgC
    tempMinAvgC
    precipitationAvgMm
    wetDayFraction
    sampleYears
  }
`;

/**
 * The outlook, of whichever kind is available.
 *
 * `kind` is non-null and always selected: it is what stops an average being
 * rendered as a forecast, so it must never be optional at the call site.
 */
export const OUTLOOK_FIELDS = gql`
  ${FORECAST_FIELDS}
  ${CLIMATE_FIELDS}
  fragment OutlookFields on Outlook {
    kind
    forecast {
      ...ForecastFields
    }
    climate {
      ...ClimateFields
    }
  }
`;

const AREAS = gql`
  query Areas {
    areas {
      id
      slug
      name
      timezone
    }
  }
`;

const AREA_OUTLOOKS = gql`
  ${OUTLOOK_FIELDS}
  query AreaOutlooks(
    $areaSlug: String!
    $fromLocalDate: String!
    $toLocalDate: String!
  ) {
    areaOutlooks(
      areaSlug: $areaSlug
      fromLocalDate: $fromLocalDate
      toLocalDate: $toLocalDate
    ) {
      localDate
      outlook {
        ...OutlookFields
      }
    }
  }
`;

export const areaQueries = {
  all: () => ["areas"] as const,
  list: () =>
    queryOptions({
      queryKey: [...areaQueries.all(), "list"] as const,
      queryFn: () => gqlClient.request<{ areas: Area[] }>(AREAS),
      /*
       * Seeded reference data with no mutations at all, so this is as close to
       * immutable as anything in the app — refetching it per mount would be pure
       * waste. Only a deployment changes it.
       */
      staleTime: Infinity,
    }),
  /**
   * Per-day outlooks for one area's week — the tab strip.
   *
   * Keyed by the local date range rather than by a week number, so the cache entry
   * says exactly what it holds and two different weeks can never collide.
   *
   * Days missing from the response simply have no chip; the component looks each
   * date up by name rather than by index.
   */
  outlooks: (areaSlug: string, fromLocalDate: string, toLocalDate: string) =>
    queryOptions({
      queryKey: [
        ...areaQueries.all(),
        "outlooks",
        areaSlug,
        fromLocalDate,
        toLocalDate,
      ] as const,
      queryFn: () =>
        gqlClient.request<{ areaOutlooks: AreaDayOutlook[] }>(AREA_OUTLOOKS, {
          areaSlug,
          fromLocalDate,
          toLocalDate,
        }),
      /*
       * Five minutes. Forecasts genuinely change through the day, but not between
       * two clicks of the week arrows — and the server caches upstream responses
       * anyway, so a refetch here is cheap. This mainly stops navigating
       * back-and-forth across weeks from re-requesting on every arrow press.
       */
      staleTime: 5 * 60 * 1000,
    }),
};
